// apps/web/src/routes/(showcase)/showcase/gallery.tsx
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { GalleryFilterChips } from "~/components/showcase/gallery/filter-chips";
import { GalleryGrid } from "~/components/showcase/gallery/gallery-grid";
import { GalleryLightbox } from "~/components/showcase/gallery/lightbox";
import { ShowcasePageHeader } from "~/components/showcase/showcase-page-header";
import { seo } from "~/lib/seo";
import {
  GALLERY_IMAGES,
  type GalleryCategory,
} from "~/lib/showcase/gallery-data";
import {
  generateStructuredDataGraph,
  getShowcasePageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(showcase)/showcase/gallery")({
  component: GalleryPage,
  head: () => {
    const title = m.meta_showcase_gallery_title();
    const description = m.meta_showcase_gallery_description();
    const seoData = seo({
      // The hospitality booking demo no longer matches what Lindevo sells.
      // Kept working and crawlable, but out of the index until it is ported
      // out to apps/mockup. Also removed from sitemap.xml.
      noindex: true,
      description,
      path: "/showcase/gallery",
      title,
    });
    const structuredData = generateStructuredDataGraph(
      getShowcasePageSchemas({
        description,
        pageName: title,
        path: "/showcase/gallery",
      })
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [{ children: structuredData, type: "application/ld+json" }],
    };
  },
});

function GalleryPage() {
  const [filter, setFilter] = useState<GalleryCategory | "all">("all");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered = useMemo(
    () =>
      filter === "all"
        ? GALLERY_IMAGES
        : GALLERY_IMAGES.filter((img) => img.category === filter),
    [filter]
  );

  return (
    <div className="mx-auto max-w-6xl overflow-x-hidden px-6 py-12">
      <ShowcasePageHeader
        eyebrow={m.showcase_gallery_eyebrow()}
        lead={m.showcase_gallery_lead()}
        title={m.showcase_gallery_title()}
      />

      <div className="mb-6">
        <GalleryFilterChips
          active={filter}
          onChange={(next) => {
            setFilter(next);
            setLightboxIndex(null);
          }}
        />
      </div>

      <GalleryGrid
        images={filtered}
        onSelect={(index) => setLightboxIndex(index)}
      />

      {lightboxIndex === null ? null : (
        <GalleryLightbox
          images={filtered}
          index={lightboxIndex}
          onChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
